<template>
  <woot-modal
    :show="isVisible"
    :on-close="handleCloseRequest"
    :close-on-backdrop-click="false"
    :header-title="$t('IMAGE_LIBRARY.UPLOAD_IMAGES')"
  >
    <div
      class="w-full max-w-3xl mx-auto"
      :class="showEdit ? 'blur-sm pointer-events-none' : ''"
      :aria-hidden="showEdit ? 'true' : 'false'"
    >
      <div class="flex flex-col rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl max-h-[75vh] overflow-hidden">
        <div class="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div
            class="rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800 p-6 text-center cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/80 transition-colors"
            @click="openPicker"
            @dragover.prevent
            @drop.prevent="onDrop"
          >
            <fluent-icon icon="arrow-upload" size="24" class="text-indigo-600 dark:text-indigo-400 mb-2" />
            <div class="text-sm text-slate-700 dark:text-slate-200">{{ $t('IMAGE_LIBRARY.UPLOAD_DROP') }}</div>
            <div class="text-xs text-slate-500 dark:text-slate-400 mt-1">{{ $t('IMAGE_LIBRARY.UPLOAD_ACCEPT') }}</div>
            <div class="text-xs text-indigo-600 dark:text-indigo-400 mt-2 font-medium">{{ $t('IMAGE_LIBRARY.UPLOAD_TIP') }}</div>
            <input ref="fileInput" type="file" class="hidden" multiple accept="image/*" @change="onFiles" />
          </div>

          <div v-if="files.length" class="space-y-2">
            <div class="text-sm text-slate-600 dark:text-slate-300">{{ files.length }} {{ $t('IMAGE_LIBRARY.IMAGES_SELECTED') }}</div>
            <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              <div
                v-for="(f, idx) in files"
                :key="idx"
                class="border border-slate-200 dark:border-slate-700 rounded-lg p-2 bg-white dark:bg-slate-900/80"
              >
                <div
                  class="aspect-square rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 mb-2 cursor-pointer"
                  @click="openEdit(idx)"
                >
                  <img :src="f.url" class="w-full h-full object-cover" @error="onImgError(idx)" />
                </div>
                <div class="text-xs text-slate-700 dark:text-slate-200 truncate">{{ f.title || f.file.name }}</div>
                <div class="text-[11px] text-slate-500 dark:text-slate-400">{{ humanSize(f.file.size) }}</div>
                <div class="mt-1 flex justify-center gap-1">
                  <woot-button size="small" variant="smooth" color-scheme="secondary" :title="$t('IMAGE_LIBRARY.ACTIONS.EDIT')" @click="openEdit(idx)">
                    <fluent-icon icon="edit" size="16" />
                  </woot-button>
                  <woot-button size="small" variant="smooth" color-scheme="secondary" :title="$t('IMAGE_LIBRARY.ACTIONS.DELETE')" @click="removeAt(idx)">
                    <fluent-icon icon="delete" size="16" />
                  </woot-button>
                </div>
              </div>
            </div>
          </div>
          <div v-if="files.length" class="text-xs text-slate-500 dark:text-slate-400">
            {{ `Nhấn "${$t('IMAGE_LIBRARY.UPLOAD')}" để bắt đầu tải ${files.length} ảnh.` }}
          </div>
        </div>

        <div class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 flex justify-center gap-3">
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            :disabled="isSubmitting"
            @click="handleCloseRequest"
          >
            {{ $t('GENERAL.CANCEL') }}
          </woot-button>
          <woot-button
            variant="smooth"
            color-scheme="primary"
            :disabled="!files.length || isSubmitting"
            :is-loading="isSubmitting"
            @click="upload"
          >
            {{ $t('IMAGE_LIBRARY.UPLOAD') }}{{ files.length ? ` (${files.length})` : '' }}
          </woot-button>
        </div>
      </div>
    </div>
  </woot-modal>
  
  <!-- Inline per-file edit modal -->
  <div v-if="showEdit" class="modal-backdrop" @click.self="closeEdit">
    <div class="modal-card p-4">
      <div class="text-sm font-medium text-gray-900 mb-3">{{ $t('IMAGE_LIBRARY.EDIT_ITEM') }}</div>
      <div class="space-y-3">
        <div>
          <woot-input
            v-model="editForm.title"
            :label="$t('IMAGE_LIBRARY.FIELDS.TITLE')"
            :required="true"
            :placeholder="$t('IMAGE_LIBRARY.FIELDS.TITLE_PLACEHOLDER')"
            :error="v$.editForm.title.$error ? $t('IMAGE_LIBRARY.FIELDS.TITLE_ERROR') : ''"
            :class="{ error: v$.editForm.title.$error }"
            @blur="v$.editForm.title.$touch"
          />
        </div>
        <div>
          <FormLabel
            :label="$t('IMAGE_LIBRARY.FIELDS.DESCRIPTION')"
            :required="false"
            :has-error="false"
          />
          <TextArea
            v-model="editForm.description"
            name="image-upload-edit-description"
            :placeholder="$t('IMAGE_LIBRARY.FIELDS.DESCRIPTION_PLACEHOLDER')"
            :max-length="1000"
            show-character-count
            auto-height
            :resize="true"
            :min-height="'120px'"
            rows="6"
            :disabled="isSubmitting"
          />
        </div>
      </div>
      <div class="mt-4 flex justify-center gap-2">
        <button class="input-base bg-slate-100 dark:bg-slate-700 !text-slate-700 dark:!text-slate-300 rounded-lg" :disabled="isSubmitting" @click="closeEdit">{{ $t('GENERAL.CANCEL') }}</button>
        <button class="input-base bg-n-main-1 text-white rounded-lg" :is-loading="isSubmitting" :disabled="!editForm.title" @click="saveEdit">{{ $t('GENERAL.SAVE') }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

export default {
  name: 'ItemUploadModal',
  setup() {
    return { v$: useVuelidate() };
  },
  props: {
    show: { type: Boolean, default: false },
    accountId: { type: [String, Number], required: true },
    libraryId: { type: [String, Number], required: true },
  },
  emits: ['close', 'uploaded'],
  components: { TextArea },

  data() {
    return {
      isVisible: false,
      files: [], // [{ file, url, title?, description? }]
      isSubmitting: false,
      progress: 0,
      description: '',
      showEdit: false,
      editingIndex: -1,
      editForm: { title: '', description: '' },
    }
  },
  validations: {
    editForm: {
      title: { required },
    },
  },
  computed: {
    uploadLabel() {
      if (this.isSubmitting) return `Đang tải... ${this.progress}%`
      return this.$t('GENERAL.UPLOAD') + (this.files.length ? ` (${this.files.length})` : '')
    }
  },
  watch: {
    show: {
      immediate: true,
      handler(value) {
        this.isVisible = value;
        if (!value) {
          this.resetTemporaryState();
        }
      }
    }
  },

  methods: {
    getDiscardMessage() {
      const messageKey = 'IMAGE_LIBRARY.CONFIRM_DISCARD_UPLOADS';
      if (typeof this.$te === 'function' && this.$te(messageKey)) {
        return this.$t(messageKey);
      }
      return 'You have selected images that are not uploaded yet. Leave without uploading?';
    },

    getRemoveMessage() {
      const messageKey = 'IMAGE_LIBRARY.CONFIRM_REMOVE_IMAGE';
      if (typeof this.$te === 'function' && this.$te(messageKey)) {
        return this.$t(messageKey);
      }
      return 'Remove this image from the upload list?';
    },

    handleCloseRequest() {
      if (this.isSubmitting) {
        return;
      }

      if (this.files.length) {
        const confirmMessage = this.getDiscardMessage();
        if (!window.confirm(confirmMessage)) {
          this.isVisible = false;
          this.$nextTick(() => {
            this.isVisible = true;
          });
          return;
        }
      }

      this.performClose();
    },

    performClose() {
      this.resetTemporaryState();
      this.isVisible = false;
      this.$emit('close');
    },

    resetTemporaryState() {
      this.revokeAll();
      this.description = '';
      this.progress = 0;
      this.showEdit = false;
      this.editingIndex = -1;
      this.editForm = { title: '', description: '' };
    },
    openPicker() { if (this.$refs.fileInput) { this.$refs.fileInput.click() } },
    onFiles(e) {
      const list = Array.from(e.target.files || [])
      this.appendFiles(list)
      e.target.value = ''
    },
    onDrop(e) {
      const list = Array.from(e.dataTransfer.files || [])
      this.appendFiles(list)
    },
    appendFiles(list) {
      const images = list.filter(f => f && f.type && f.type.startsWith('image/'))
      const nonImages = list.length - images.length
      if (nonImages > 0) {
        this.$store.dispatch('setBanner', { type: 'warning', message: this.$t('GENERAL.INVALID_FILE_TYPE') || 'Tệp không hợp lệ' })
      }
      images.forEach(f => {
        const baseName = this.stripExtension(f.name)
        try {
          const url = URL.createObjectURL(f)
          this.files.push({ file: f, url, title: baseName, description: this.description || '' })
        } catch (e) {
          const reader = new FileReader()
          reader.onload = ev => {
            this.files.push({ file: f, url: ev.target.result, title: baseName, description: this.description || '' })
          }
          reader.readAsDataURL(f)
        }
      })
    },
    removeAt(idx) {
      const file = this.files[idx]
      if (!file) return

      if (!window.confirm(this.getRemoveMessage())) {
        return
      }

      if (file?.url) URL.revokeObjectURL(file.url)
      this.files.splice(idx, 1)
    },
    revokeAll() {
      this.files.forEach(f => f?.url && URL.revokeObjectURL(f.url))
      this.files = []
    },
    async upload() {
      try {
        this.isSubmitting = true
        this.progress = 0
        const form = new FormData()
        this.files.forEach(f => form.append('files[]', f.file))
        // per-file metadata
        this.files.forEach(f => form.append('titles[]', f.title || this.stripExtension(f.file.name)))
        this.files.forEach(f => form.append('descriptions[]', (f.description || '').toString()))
        if (this.description) form.append('description', this.description)
        const response = await window.axios.post(
          `/api/v1/accounts/${this.accountId}/image_libraries/${this.libraryId}/image_items/bulk_upload`,
          form,
          {
            onUploadProgress: (e) => {
              if (e && e.total) {
                this.progress = Math.round((e.loaded * 100) / e.total)
              }
            }
          }
        )       
        if (response.data.error_count > 0 || response.data.error) {
          // Show only the backend error message via useAlert
          const errorMessage = response.data.error
          if (errorMessage) {
            useAlert(errorMessage)
          }
          
          if (response.data.success_count > 0) {
            this.revokeAll()
            this.$emit('uploaded')
          }
          return
        }
        
        this.$store.dispatch('setBanner', { 
          type: 'success', 
          message: response.data.message || 'Files uploaded successfully' 
        })
        this.revokeAll()
        this.$emit('uploaded')
      } catch (error) {
        const errorMessage = error?.response?.data?.error
        if (errorMessage) {
          useAlert(errorMessage, { type: 'error' })
        } else {
          useAlert('Failed to upload files', { type: 'error' })
        }
      } finally {
        this.isSubmitting = false
        this.progress = 0
      }
    },
    openEdit(idx) {
      const f = this.files[idx]
      if (!f) return
      this.editingIndex = idx
      this.editForm.title = f.title || this.stripExtension(f.file.name)
      this.editForm.description = f.description || ''
      this.showEdit = true
    },
    closeEdit() {
      this.showEdit = false
      this.editingIndex = -1
      this.editForm = { title: '', description: '' }
      this.v$.$reset()
    },
    saveEdit() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      
      if (this.editingIndex < 0) return
      const f = this.files[this.editingIndex]
      if (!f) return
      f.title = this.editForm.title || this.stripExtension(f.file.name)
      f.description = this.editForm.description || ''
      this.closeEdit()
    },
    onImgError(idx) {
      const f = this.files[idx]
      if (!f) return
      f.url = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw=='
    },
    humanSize(bytes) {
      if (!bytes) return ''
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      const sizes = ['B', 'KB', 'MB', 'GB']
      return `${(bytes / (1024 ** i)).toFixed(2)} ${sizes[i]}`
    },
    stripExtension(filename) {
      if (!filename) return ''
      const dotIndex = filename.lastIndexOf('.')
      if (dotIndex <= 0) return filename
      return filename.slice(0, dotIndex)
    }
  }
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}
.modal-card {
  @apply bg-n-background border border-n-gray-1 dark:border-n-gray-8 rounded-xl shadow-2xl w-full max-w-[520px];
}
</style>
